/**
 * localStorage wrapper for the web session token.
 *
 * Single key `ori.session.v1`. JSON-encoded so we can carry minimal
 * cached identity (actor_id, self_name) for instant first paint
 * without waiting for /api/me. The server is the source of truth —
 * cached identity is purely a UX optimization.
 */

const KEY = 'ori.session.v1';

export interface CachedSession {
  session_token: string;
  actor_id: string;
  self_name: string;
  cached_at: string;
}

// Every touch of `localStorage` — the name itself included — stays inside the
// try: Safari with "Block All Cookies" throws SecurityError ("The operation is
// insecure.") on reading the global, so a `typeof localStorage` guard outside
// the try took the whole SPA down from SessionProvider's first render (prod
// 2026-08-29 → 09-21: eleven iOS browsers saw "Page failed to render").
// Without the global (SSR) the read throws ReferenceError, caught the same way.

export function readCachedSession(): CachedSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedSession>;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.session_token !== 'string' || !parsed.session_token) return null;
    return {
      session_token: parsed.session_token,
      actor_id: typeof parsed.actor_id === 'string' ? parsed.actor_id : '',
      self_name: typeof parsed.self_name === 'string' ? parsed.self_name : '',
      cached_at: typeof parsed.cached_at === 'string' ? parsed.cached_at : '',
    };
  } catch {
    return null;
  }
}

export function writeCachedSession(value: Omit<CachedSession, 'cached_at'>): void {
  try {
    const payload: CachedSession = { ...value, cached_at: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // Storage quota / disabled — silent fail; session lives in memory only.
  }
}

export function clearCachedSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/**
 * Everything this browser keeps for the signed-in account (2026-10-21 Riley:
 * Log out on the closed screen): the cached session, unsent drafts and photos,
 * the letters / notebook / announcement marks, the text door's pending wait.
 * Keys are ours by prefix (`ember…`, `ori.`); the cookie choice
 * (`ori_cookie_consent`) belongs to the browser, not the account, and stays.
 */
export function clearLocalAccountData(): void {
  for (const area of ['localStorage', 'sessionStorage'] as const) {
    try {
      const storage = window[area];
      const keys: string[] = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key && (key.startsWith('ember') || key.startsWith('ori.'))) keys.push(key);
      }
      for (const key of keys) storage.removeItem(key);
    } catch {
      // Storage refused (Safari "Block All Cookies") — nothing kept, nothing to clear.
    }
  }
}
